import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Input, Popconfirm, message } from "antd";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { createData, getData } from "../../util/API";
import { KYC_REFRESH_EVENT } from "./kycBlock";
import {
  DOCK_FIRST_PEEK_DELAY_MS,
  DOCK_PEEK_EVERY_MS,
  DOCK_PEEK_MS,
  HIDDEN_ROLES,
  POLL_MS,
  TYPE_FILTERS,
  hiddenStorageKey,
} from "./constants";
import { attempterCardClass, rejectedByLine, resubmitKyc } from "./kycRejection";
import "./KycVerificationNotifications.less";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function readHidden() {
  try {
    return localStorage.getItem(hiddenStorageKey()) === "1";
  } catch (e) {
    return false;
  }
}

function writeHidden(hidden) {
  try {
    localStorage.setItem(hiddenStorageKey(), hidden ? "1" : "0");
  } catch (e) {
    // storage unavailable: hidden state lasts for this page only
  }
}

function matchesType(partner, filterKey) {
  const filter = TYPE_FILTERS.find((f) => f.key === filterKey);
  if (!filter || !filter.types) return true;
  return (partner.attempt_types || []).some((t) => filter.types.includes(t));
}

const KycVerificationNotifications = () => {
  const history = useHistory();
  const userRole = localStorage.getItem("user_role") || "";
  const enabled = Boolean(localStorage.getItem("token")) && !HIDDEN_ROLES.has(userRole);

  const [payload, setPayload] = useState(null);
  const [hidden, setHidden] = useState(readHidden);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState("attempts");
  const [expanded, setExpanded] = useState({});
  const [acknowledging, setAcknowledging] = useState(null);
  const [resubmitting, setResubmitting] = useState(null);
  const [peeking, setPeeking] = useState(false);
  const peekTimerRef = useRef(null);
  const attentionRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await getData("/finance/kyc-notifications/");
      if (res && res.data && res.data.status === 200) setPayload(res.data);
    } catch (e) {
      // silent poll failure; next poll retries
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchNotifications();
    const timer = setInterval(fetchNotifications, POLL_MS);
    window.addEventListener("focus", fetchNotifications);
    window.addEventListener(KYC_REFRESH_EVENT, fetchNotifications);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", fetchNotifications);
      window.removeEventListener(KYC_REFRESH_EVENT, fetchNotifications);
    };
  }, [enabled, fetchNotifications]);

  const view = (payload && payload.view) || "none";
  const partners = (payload && payload.partners) || [];
  const isFinance = view === "finance";
  const q = query.trim().toLowerCase();

  const visible = useMemo(() => {
    let list = partners.filter(
      (p) => !q || (p.name || "").toLowerCase().includes(q) || (p.hrvid || "").toLowerCase().includes(q)
    );
    if (isFinance) {
      list = list.filter((p) => matchesType(p, typeFilter));
      list = [...list].sort((a, b) =>
        sort === "waiting"
          ? moment(a.waiting_since).valueOf() - moment(b.waiting_since).valueOf()
          : b.attempt_total - a.attempt_total
      );
    }
    return list;
  }, [partners, q, isFinance, typeFilter, sort]);

  const totals = (payload && payload.totals) || {};
  const verifiedCount = isFinance ? 0 : totals.verified || 0;
  const pendingCount = isFinance ? partners.length : totals.pending || 0;
  const rejectedCount = isFinance ? 0 : totals.rejected || 0;
  const attention = pendingCount + verifiedCount;

  // Docked tab: slide out briefly so a hidden notification still asks for attention.
  const peek = useCallback(() => {
    setPeeking(true);
    clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => setPeeking(false), DOCK_PEEK_MS);
  }, []);

  useEffect(() => {
    if (!enabled || !hidden) return undefined;
    const first = setTimeout(peek, DOCK_FIRST_PEEK_DELAY_MS);
    const every = setInterval(peek, DOCK_PEEK_EVERY_MS);
    return () => {
      clearTimeout(first);
      clearInterval(every);
      clearTimeout(peekTimerRef.current);
    };
  }, [enabled, hidden, peek]);

  useEffect(() => {
    if (hidden && attention > attentionRef.current) peek();
    attentionRef.current = attention;
  }, [attention, hidden, peek]);

  const setHiddenState = (value) => {
    setHidden(value);
    writeHidden(value);
    if (!value) setPeeking(false);
  };

  const acknowledge = async (partnerId) => {
    setAcknowledging(partnerId);
    try {
      const res = await createData(`/finance/kyc-notifications/${partnerId}/acknowledge/`, {});
      if (res && res.data && res.data.status === 200) {
        fetchNotifications();
      } else {
        message.error((res && res.data && res.data.message) || "Could not clear this notification");
      }
    } catch (error) {
      message.error(
        (error && error.response && error.response.data && error.response.data.message) ||
          "Could not clear this notification"
      );
    } finally {
      setAcknowledging(null);
    }
  };

  const resubmit = async (partnerId) => {
    setResubmitting(partnerId);
    const result = await resubmitKyc(partnerId);
    setResubmitting(null);
    if (result.ok) {
      message.success(result.message || "Sent to accounts for verification");
      fetchNotifications();
    } else {
      message.error(result.message);
    }
  };

  if (!enabled || view === "none" || partners.length === 0) return null;

  const allDone = pendingCount === 0;

  if (hidden) {
    const tabCount = allDone ? verifiedCount : pendingCount;
    const pillClass = peeking
      ? "kyc-notice-pill kyc-notice-pill--docked kyc-notice-pill--peek"
      : "kyc-notice-pill kyc-notice-pill--docked";
    return (
      <div className="kyc-notice-dock">
        <button
          type="button"
          className={pillClass}
          onClick={() => setHiddenState(false)}
          aria-label={`Show KYC notifications: ${pendingCount} pending${verifiedCount ? `, ${verifiedCount} verified` : ""}`}
        >
          <span className="kyc-notice-pill-tab">
            <span className="kyc-notice-pill-icon">
              <Icon type="safety-certificate" />
              <span className={verifiedCount ? "kyc-notice-dot kyc-notice-dot--done" : "kyc-notice-dot"} />
            </span>
            <span className={allDone ? "kyc-notice-tab-count kyc-notice-tab-count--done" : "kyc-notice-tab-count"}>
              {tabCount > 99 ? "99+" : tabCount}
            </span>
          </span>
          <span className="kyc-notice-pill-more">
            <span className="kyc-notice-pill-label">KYC</span>
            {pendingCount > 0 ? <span className="kyc-notice-badge">{pendingCount} pending</span> : null}
            {verifiedCount > 0 ? <span className="kyc-notice-badge kyc-notice-badge--done">{verifiedCount} verified</span> : null}
            <Icon type="down" className="kyc-notice-pill-caret" />
          </span>
        </button>
      </div>
    );
  }

  const financeCard = (p) => {
    const open = Boolean(expanded[p.partner_id]);
    const people = p.people || [];
    return (
      <article key={p.partner_id} className="kyc-notice-card kyc-notice-card--pending">
        <div className="kyc-notice-card-top">
          <span className="kyc-notice-card-name">{p.name}</span>
          <span className="kyc-notice-card-age">Waiting {moment(p.waiting_since).fromNow(true)}</span>
        </div>
        <div className="kyc-notice-card-hrvid">{p.hrvid || "No HRVID"}</div>
        <div className="kyc-notice-chips">
          <span className="kyc-notice-chip kyc-notice-chip--blocked">
            <Icon type="lock" /> {plural(p.attempt_total, "attempt")}
          </span>
          {p.last_invoice_request ? <span className="kyc-notice-chip">{p.last_invoice_request}</span> : null}
        </div>
        {p.unverified_items && p.unverified_items.length ? (
          <div className="kyc-notice-missing">
            Not verified: <strong>{p.unverified_items.join(", ")}</strong>
          </div>
        ) : null}
        {open ? (
          <div className="kyc-notice-details">
            {people.map((person) => (
              <div key={person.id === null ? "none" : person.id} className="kyc-notice-person">
                <span className="kyc-notice-person-name">{person.name}</span>
                <span className="kyc-notice-person-count">{plural(person.attempt_count, "attempt")}</span>
                <span className="kyc-notice-person-time">{moment(person.last_attempt_at).fromNow()}</span>
              </div>
            ))}
            {p.projects && p.projects.length ? (
              <div className="kyc-notice-projects">Projects: {p.projects.join(", ")}</div>
            ) : null}
          </div>
        ) : null}
        <div className="kyc-notice-actions">
          <Button
            type="primary"
            size="small"
            className="kyc-notice-primary"
            disabled={!p.kyc_id}
            onClick={() => history.push(`/kyc-verify/${p.kyc_id}`)}
          >
            Verify KYC <Icon type="export" />
          </Button>
          <Button
            type="link"
            size="small"
            className="kyc-notice-toggle"
            onClick={() => setExpanded((prev) => ({ ...prev, [p.partner_id]: !open }))}
          >
            {open ? "Hide details" : `Who tried (${people.length})`} <Icon type={open ? "up" : "down"} />
          </Button>
        </div>
      </article>
    );
  };

  const attempterCard = (p) => {
    const done = p.status === "verified";
    const rejected = p.status === "rejected";
    const others = p.others_count === 1 ? "1 other person" : `${p.others_count} other people`;
    return (
      <article key={p.partner_id} className={attempterCardClass(p.status)}>
        <div className="kyc-notice-card-top">
          <span className="kyc-notice-card-name">{p.name}</span>
          <span className="kyc-notice-card-age">
            {done ? moment(p.verified_at).fromNow() : `Waiting ${moment(p.waiting_since).fromNow(true)}`}
          </span>
        </div>
        <div className="kyc-notice-card-hrvid">{p.hrvid || "No HRVID"}</div>
        <div className="kyc-notice-chips">
          {done ? (
            <span className="kyc-notice-chip kyc-notice-chip--done">
              <Icon type="check-circle" /> KYC verified
            </span>
          ) : rejected ? (
            <span className="kyc-notice-chip kyc-notice-chip--rejected">
              <Icon type="close-circle" /> Rejected by accounts
            </span>
          ) : (
            <span className="kyc-notice-chip kyc-notice-chip--pending">
              <Icon type="warning" /> Pending with Finance
            </span>
          )}
          <span className={done ? "kyc-notice-chip" : "kyc-notice-chip kyc-notice-chip--blocked"}>
            <Icon type="lock" /> {done ? `${plural(p.attempt_count, "attempt")} made` : `${p.attempt_count} of your attempts`}
          </span>
        </div>
        {!done && p.others_count > 0 ? <div className="kyc-notice-note">Also blocked for {others}</div> : null}
        {rejected ? (
          <div className="kyc-notice-rejected">
            <span>Waiting for the partner's KYC to be corrected</span>
            <div>
              Incorrect: <strong>{(p.rejected_items || []).join(", ")}</strong>
            </div>
            <em>{rejectedByLine(p)}</em>
          </div>
        ) : null}
        {!done && !rejected && p.unverified_items && p.unverified_items.length ? (
          <div className="kyc-notice-missing">
            Not verified: <strong>{p.unverified_items.join(", ")}</strong>
          </div>
        ) : null}
        {done ? (
          <div className="kyc-notice-verified">
            Verified by {p.verified_by_name || "Finance"} · {moment(p.verified_at).format("D MMM, h:mm A")}
            <span>You can raise the invoice again.</span>
          </div>
        ) : null}
        {done ? (
          <div className="kyc-notice-actions">
            <Button
              type="primary"
              size="small"
              className="kyc-notice-primary kyc-notice-ok"
              loading={acknowledging === p.partner_id}
              onClick={() => acknowledge(p.partner_id)}
            >
              OK
            </Button>
          </div>
        ) : null}
        {rejected ? (
          <div className="kyc-notice-actions">
            <Popconfirm
              title="Send the corrected KYC back to accounts?"
              okText="Send"
              onConfirm={() => resubmit(p.partner_id)}
            >
              <Button
                type="primary"
                size="small"
                className="kyc-notice-primary kyc-notice-resubmit"
                loading={resubmitting === p.partner_id}
              >
                Corrected, send to accounts
              </Button>
            </Popconfirm>
          </div>
        ) : null}
      </article>
    );
  };

  return (
    <div className="kyc-notice-anchor">
      <section className="kyc-notice-panel" aria-label="Partner KYC notifications">
        <header className="kyc-notice-header">
          <span className={allDone ? "kyc-notice-header-icon kyc-notice-header-icon--done" : "kyc-notice-header-icon"}>
            <Icon type="safety-certificate" />
          </span>
          <div className="kyc-notice-header-text">
            <span className="kyc-notice-title">{isFinance ? "Partner KYC verification" : "Partner KYC status"}</span>
            <span className="kyc-notice-sub">
              {isFinance ? "Invoices are blocked until KYC is verified" : "Your invoices waiting on partner KYC"}
            </span>
          </div>
          <span className={allDone ? "kyc-notice-count kyc-notice-count--done" : "kyc-notice-count"}>{partners.length}</span>
          <button
            type="button"
            className="kyc-notice-hide"
            onClick={() => setHiddenState(true)}
            title="Hide. Stays until KYC is verified."
            aria-label="Hide KYC notifications"
          >
            <Icon type="up" />
          </button>
        </header>

        <div className="kyc-notice-toolbar">
          <div className="kyc-notice-toolbar-row">
            <span className="kyc-notice-summary">
              {isFinance
                ? `${plural(totals.attempts || 0, "attempt")} · ${plural(partners.length, "partner")}`
                : `${verifiedCount ? `${verifiedCount} verified · ` : ""}${
                    rejectedCount ? `${rejectedCount} rejected by accounts · ` : ""
                  }${pendingCount - rejectedCount} pending with Finance`}
            </span>
            {isFinance ? (
              <button type="button" className="kyc-notice-link" onClick={() => history.push("/finance")}>
                KYC Verification page <Icon type="arrow-right" />
              </button>
            ) : null}
          </div>
          <div className="kyc-notice-search-row">
            <Input
              size="small"
              allowClear
              prefix={<Icon type="search" />}
              placeholder="Search partner or HRVID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {isFinance ? (
              <button
                type="button"
                className="kyc-notice-sort"
                onClick={() => setSort(sort === "waiting" ? "attempts" : "waiting")}
              >
                {sort === "waiting" ? "Longest waiting" : "Most attempts"} <Icon type="down" />
              </button>
            ) : null}
          </div>
          {isFinance ? (
            <div className="kyc-notice-filters">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={typeFilter === f.key ? "kyc-notice-filter kyc-notice-filter--active" : "kyc-notice-filter"}
                  onClick={() => setTypeFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="kyc-notice-list">
          {visible.map((p) => (isFinance ? financeCard(p) : attempterCard(p)))}
          {visible.length === 0 ? (
            <div className="kyc-notice-empty">
              {q ? `No partner matches "${query}"` : "No partner with this invoice type"}
            </div>
          ) : null}
        </div>

        <footer className="kyc-notice-footer">
          <Icon type="lock" />
          <span>
            {isFinance
              ? "Stays until each partner's KYC is verified. Hide keeps it as a pill."
              : "Pending partners clear once Finance verifies. Verified ones need your OK."}
          </span>
        </footer>
      </section>
    </div>
  );
};

export default KycVerificationNotifications;
